import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import { deleteWMS2020_R01, disableWMS2020_R01, downloadExcel_WMS2020_R01, getPdfBlob_WMS2020_R01, getWMS2020_R01AllCount, getWMS2020_R01List, patchWMS2020_R01, postWMS2020_R01 } from "./Api_WMS2020_R01";
import { defaultWMS2020_R01Filter, WMS2020_R01Filter, useWMS2020_R01Store, defaultWMS2020_R01ProcessFilter, WMS2020_R01ProcessFilter } from "./Store_WMS2020_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { defaultWMS2020_R01Dto, WMS2020_R01Dto } from "./Dto_WMS2020_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { refetchUpdateList_Excelgrid } from "src/utils/axios/reFetch";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, fetchFilterConfig_refetch, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import useUserStore from "src/store/useUserStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<WMS2020_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const WMS2020_R01Content = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		WMS2020_R01Filter, setWMS2020_R01Filter,
		WMS2020_R01PastFilter, setWMS2020_R01PastFilter,
		WMS2020_R01ProcessFilter, setWMS2020_R01ProcessFilter,
		WMS2020_R01List, setWMS2020_R01List, insertWMS2020_R01List, removeWMS2020_R01List,
		deletedWMS2020_R01List, addDeletedWMS2020_R01List, removeDeletedWMS2020_R01List,
		selectedWMS2020_R01List, setSelectedWMS2020_R01List, removeSelectedWMS2020_R01List,
		originalWMS2020_R01List, setOriginalWMS2020_R01List,
		WMS2020_R01DetailList, setWMS2020_R01DetailList, insertWMS2020_R01DetailList, removeWMS2020_R01DetailList,
		deletedWMS2020_R01DetailList, addDeletedWMS2020_R01DetailList, removeDeletedWMS2020_R01DetailList,
		selectedWMS2020_R01DetailList, setSelectedWMS2020_R01DetailList, removeSelectedWMS2020_R01DetailList,
		originalWMS2020_R01DetailList, setOriginalWMS2020_R01DetailList,
		allCount, setAllCount,
		initData,
		setScrollTop,
		setDetailScrollTop,
	} = useWMS2020_R01Store();

	const { fetchOrgCdCode, wmsCdList, warehouseCdList } = useCommonCodeStore();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	const { company, userInfo } = useUserStore();

	const setAuth = useActionbarAuthStore((state) => state.setAuth);

	// 권한 정보 로드
	useFetchActionbarAuth("WMS2020_R01");

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});
	
	
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_WMS2020_R01(
			'',
			getParams(WMS2020_R01Filter),
			WMS2020_R01List.length ?? 0
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '생산계획 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [WMS2020_R01Filter, WMS2020_R01List])

	

	const handlePrintClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_WMS2020_R01(
				'',
				getParams(WMS2020_R01Filter),
				WMS2020_R01List.length ?? 0,
				company?.compCd || ''
			);

			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
					// showWarning("QZ 출력 실패로 브라우저 미리보기로 대체합니다.");
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [WMS2020_R01Filter, WMS2020_R01List, company?.compCd, printPdfFromBlob, setLoadingSpinner, setMessage]);

	const getParams = (filter: WMS2020_R01Filter) => {
		const params: Partial<WMS2020_R01Filter> = {
			logicCenter: filter.logicCenter || undefined,
			warehouseCd: filter.warehouseCd || undefined,
			brandCd: filter.brandCd || undefined,
			itemCd: filter.itemCd || undefined,
		};
		return params;
	}
	
	//신규 행 추가시 기본적으로 세팅할 데이터를 정하고 생성
	const defaultWMS2020_R01Row = useCallback(() => {

		return {
			...defaultWMS2020_R01Dto,
			inserted: true,
		}
	}, [defaultWMS2020_R01Dto, wmsCdList, warehouseCdList]);

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getWMS2020_R01AllCount(getParams(WMS2020_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [WMS2020_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);

		if (fetchType === 'search' || messageMode === 'show') {
			setScrollTop(null);
			setDetailScrollTop(null);
		}
	
		let initialListLength = WMS2020_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setWMS2020_R01List([]);
		}

		const responseData = await getWMS2020_R01List(
			'',
			getParams(WMS2020_R01Filter),
			fetchType === 'refetch' ? initialListLength : WMS2020_R01List.length ?? 0
		);
	
		let newList = [...responseData.data.data ] as WMS2020_R01Dto[];
		setWMS2020_R01List(newList);

		const processAllCount = await renewalAllCount();
		setOriginalWMS2020_R01List(newList);
		setSelectedWMS2020_R01List([]);
		removeDeletedWMS2020_R01List(deletedWMS2020_R01List);
	
		if (newList.length === 0) {
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else {
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}
		}
	
		setWMS2020_R01PastFilter(WMS2020_R01Filter);
		setWMS2020_R01ProcessFilter(defaultWMS2020_R01ProcessFilter);
		setLoadingSpinner(false);
	}, [
		WMS2020_R01Filter,  WMS2020_R01PastFilter, WMS2020_R01List, deletedWMS2020_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalWMS2020_R01List, setSelectedWMS2020_R01List, insertWMS2020_R01List,
		defaultWMS2020_R01Row, setScrollTop, setDetailScrollTop,
	]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedWMS2020_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedWMS2020_R01List(deletedWMS2020_R01List);
			removeSelectedWMS2020_R01List();
			setWMS2020_R01List([...WMS2020_R01List]);
		} else if (selectedWMS2020_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedWMS2020_R01List(selectedWMS2020_R01List);
			removeSelectedWMS2020_R01List(selectedWMS2020_R01List);
		}
	}, [selectedWMS2020_R01List, deletedWMS2020_R01List, addDeletedWMS2020_R01List, removeDeletedWMS2020_R01List, removeSelectedWMS2020_R01List, setWMS2020_R01List, WMS2020_R01List]);

	const validateInsertData = (data: WMS2020_R01Dto, processFilter: WMS2020_R01ProcessFilter ,index: number, valid: boolean) => {

		if(!validateString(data.logicCenter)) {
			if(valid) showWarning('물류센터를 선택해주세요.');
			return false;
		}
		if(!validateString(data.warehouseCd)) {
			if(valid) showWarning('창고를 선택해주세요.');
			return false;
		}

		if(processFilter.warehouseCd === data.oldWarehouseCd && processFilter.logicCenter === data.oldLogicCenter) {
			if(valid) showWarning('동일한 물류센터와 창고로는 작업할수 없습니다.');
			return false;
		}

		if(processFilter.processType === 'split' && data.movedLotQty <= 0) {
			if(valid) showWarning('분할작업시 이동량은 0 이상이어야 합니다.');
			return false;
		}
		
		return true;
	}
	
	
	const handleSaveClick = useCallback(async () => {
		setLoadingSpinner(true);
		// 이관 lot 처리
		const modifiedList = WMS2020_R01DetailList.filter(row => !deletedWMS2020_R01List.includes(row.lotNo));
		if(modifiedList.length === 0) {
			showWarning('이동분할작업에 필요한 정보가 없습니다.');
			setLoadingSpinner(false);
			return;
		}
		
        // 수정 사용자 유효성 검증
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = WMS2020_R01DetailList.findIndex(user => user.lotNo === value.lotNo);
			if(!validateString(WMS2020_R01ProcessFilter.warehouseCd)) { showWarning('작업대상창고를 선택해주세요.'); valid = false; return; }
			if(!validateString(WMS2020_R01ProcessFilter.logicCenter)) { showWarning('작업대상물류센터를 선택해주세요.'); valid = false; return; }
			if(!validateString(WMS2020_R01ProcessFilter.processType)) { showWarning('작업 유형을 선택해주세요.'); valid = false; return; }
			if(!validateInsertData(value, WMS2020_R01ProcessFilter, index, valid)) valid = false;
			seenMenuSet.add(value.lotNo);
		});
		if(!valid) { 
			setLoadingSpinner(false);
			return; 
		}
        // 신규 사용자 유효성 검증
		seenMenuSet = new Set<string>();
		valid = true;

		const [modifiedResponse] = await Promise.all([
			modifiedList.length > 0 ? patchWMS2020_R01(modifiedList, WMS2020_R01ProcessFilter.processType) : Promise.resolve(),
		]);

		if(modifiedResponse) {
			showSuccess('이동분할정보 작업이 완료되었습니다.');
			setMessage('이동분할정보 작업이 완료되었습니다.');
			setWMS2020_R01DetailList([]);
			setSelectedWMS2020_R01DetailList([]);
		}

		setTimeout(() => { setMessage(''); }, 5000);
		setLoadingSpinner(false);
		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [
		WMS2020_R01ProcessFilter,
		deletedWMS2020_R01List,
		WMS2020_R01DetailList,  setWMS2020_R01DetailList,
		setSelectedWMS2020_R01DetailList,
		executeDataFetch, 
	]);

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedWMS2020_R01List.length === 0 && selectedWMS2020_R01List.length === 0,
			save: WMS2020_R01List.length === 0,
			print: WMS2020_R01List.length === 0,
			excel: WMS2020_R01List.length === 0
		}));
	}, [selectedWMS2020_R01List, deletedWMS2020_R01List, WMS2020_R01List]);

	return{
		buttonDisabled,
		handleExcelClick,
		handlePrintClick,
		defaultWMS2020_R01Row,
		fetchData,
		initContainerData,
		handleDeleteClick,
		handleSaveClick,
	}
}
